import { Info, Lightbulb } from 'lucide-react';
import { Button, Skeleton } from '@doccareplus/ui';
import { useTranslation } from '@doccareplus/i18n';
import { cn } from '@doccareplus/shared/lib/utils';
import type { StaffBookingSlot } from '../infrastructure/scheduling.api';

interface Props {
  providerSelected: boolean;
  durationSelected?: boolean;
  slots: StaffBookingSlot[];
  timezone?: string;
  selectedStartTime: string;
  isLoading: boolean;
  isError: boolean;
  unavailableReason?: 'ProviderAvailabilityNotConfigured' | 'NoSlotsForDate' | null;
  allowCustomTime?: boolean;
  customTimeSelected?: boolean;
  warnOnCustomTime?: boolean;
  onSelect: (startTime: string) => void;
  onSelectCustom?: () => void;
  onRetry: () => void;
}

function formatSlot(startTime: string, timezone?: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(new Date(startTime));
}

export function AvailableSlotPicker({
  providerSelected,
  durationSelected = true,
  slots,
  timezone,
  selectedStartTime,
  isLoading,
  isError,
  unavailableReason,
  allowCustomTime = false,
  customTimeSelected = false,
  warnOnCustomTime = false,
  onSelect,
  onSelectCustom,
  onRetry,
}: Props) {
  const { t } = useTranslation('appointments');
  const firstSlotTime = slots.length > 0
    ? Math.min(...slots.map((slot) => new Date(slot.startTime).getTime()))
    : null;
  const initialWindowEnd = firstSlotTime === null ? null : firstSlotTime + (60 * 60 * 1_000);
  const visibleSlots = initialWindowEnd === null
    ? []
    : slots.filter((slot) => new Date(slot.startTime).getTime() < initialWindowEnd);

  const customTimeButton = allowCustomTime ? (
    <Button
      type="button"
      variant="ghost"
      fullWidth
      aria-pressed={customTimeSelected}
      onClick={onSelectCustom}
      className={cn(
        'justify-center',
        customTimeSelected && 'border-primary bg-primary-weak text-primary',
      )}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
        <circle cx="12" cy="12" r="9" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
      </svg>
      {t('booking.availableSlots.customTime')}
    </Button>
  ) : null;

  return (
    <section className="space-y-2.5" aria-labelledby="available-time-label">
      <div>
        <p id="available-time-label" className="text-13 font-semibold text-text">
          {t('booking.availableSlots.label')}
          <span className="ml-0.5 text-danger" aria-hidden="true">*</span>
        </p>
        <p className="mt-0.5 text-12 text-text-3">{t('booking.availableSlots.hint')}</p>
      </div>

      {!providerSelected ? (
        <p className="rounded-lg bg-surface-2 px-3 py-2.5 text-12 text-text-2" role="note">
          {t('booking.availableSlots.selectProvider')}
        </p>
      ) : !durationSelected ? (
        <p className="rounded-lg bg-surface-2 px-3 py-2.5 text-12 text-text-2" role="note">
          {t('booking.availableSlots.selectDuration')}
        </p>
      ) : isLoading ? (
        <div className="grid grid-cols-3 gap-2" role="status" aria-label={t('booking.availableSlots.loading')}>
          {Array.from({ length: 6 }, (_, index) => <Skeleton key={index} className="h-9 rounded-[9px]" />)}
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-danger/25 bg-danger-bg p-3" role="alert">
          <p className="text-12 text-danger">{t('booking.availableSlots.error')}</p>
          <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={onRetry}>
            {t('common:actions.retry', { defaultValue: 'Retry' })}
          </Button>
        </div>
      ) : unavailableReason || slots.length === 0 ? (
        <div className="space-y-2.5">
          <div className="overflow-hidden rounded-lg border border-border" role="status">
            <div className="flex items-start gap-2 bg-warning-bg px-3 py-2.5">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
              <p className="text-12 text-warning">
                {unavailableReason === 'ProviderAvailabilityNotConfigured'
                  ? t('booking.availableSlots.providerNotConfigured')
                  : t('booking.availableSlots.noSlotsForDate')}
              </p>
            </div>
            {unavailableReason === 'NoSlotsForDate' && (
              <div className="flex items-start gap-2 border-t border-border bg-info-bg px-3 py-2.5">
                <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
                <p className="text-12 text-info">{t('booking.availableSlots.noSlotsForDateWalkInHint')}</p>
              </div>
            )}
          </div>
          {allowCustomTime && (
            <>
              {customTimeButton}
              <p className="rounded-lg bg-warning-bg px-3 py-2.5 text-12 text-warning" role="note">
                {t('booking.availableSlots.customTimeWarning')}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="grid grid-cols-3 gap-2" role="group" aria-label={t('booking.availableSlots.label')}>
            {visibleSlots.map((slot) => {
              const selected = slot.startTime === selectedStartTime;
              return (
                <button
                  key={slot.startTime}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onSelect(slot.startTime)}
                  className={cn(
                    'min-h-9 rounded-[9px] border px-2 py-1.5 text-13 font-semibold transition-colors active:scale-[0.98]',
                    selected
                      ? 'border-primary bg-primary text-primary-fg'
                      : 'border-border bg-surface text-text-2 hover:border-primary hover:text-primary',
                  )}
                >
                  {formatSlot(slot.startTime, timezone)}
                </button>
              );
            })}
          </div>
          {customTimeButton}
        </div>
      )}
      {providerSelected && allowCustomTime && customTimeSelected && warnOnCustomTime && slots.length > 0 && !unavailableReason && (
        <p className="rounded-lg bg-warning-bg px-3 py-2.5 text-12 text-warning" role="note">
          {t('booking.availableSlots.customTimeWarning')}
        </p>
      )}
    </section>
  );
}
